import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d6uldavb245v7h34odjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0B2C26] text-stone-300">
      {/* Newsletter strip */}
      <div className="border-b border-[#1a4a40]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-lg">Stay stoked. Get repair tips & exclusive deals.</p>
            <p className="text-stone-400 text-sm mt-1">Join 40,000+ surfers who trust PHIX Doctor.</p>
          </div>
          {submitted ? (
            <div className="flex items-center gap-2 text-[#FF6B35] font-semibold">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-check-line text-xl" />
              </div>
              You&apos;re in! Check your inbox.
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex gap-2 w-full md:w-auto"
            >
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="bg-[#1a4a40] text-white placeholder-stone-500 text-sm px-4 py-2.5 rounded-full flex-1 md:w-72 border border-[#2a6a5a] focus:outline-none focus:border-[#FF6B35]"
              />
              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap bg-[#FF6B35] text-white text-sm font-bold px-6 py-2.5 rounded-full hover:bg-[#e55a25] transition-colors cursor-pointer disabled:opacity-60"
              >
                {submitting ? 'Joining...' : 'Join Free'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        {/* Brand */}
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-sun-fill text-xl text-[#FF6B35]" />
            </div>
            <span className="font-black text-lg text-white">
              PHIX<span className="text-[#FF6B35]"> DOCTOR</span>
            </span>
          </div>
          <p className="text-stone-400 text-sm leading-relaxed">
            Sun-powered surfboard ding repair kits trusted by surfers in 60+ countries. Fix it fast. Surf again.
          </p>
          <div className="flex gap-4 mt-5">
            {['ri-instagram-line', 'ri-facebook-line', 'ri-youtube-line'].map((icon) => (
              <a key={icon} href="#" className="w-8 h-8 flex items-center justify-center bg-[#1a4a40] rounded-full hover:bg-[#FF6B35] transition-colors cursor-pointer">
                <i className={`${icon} text-sm text-white`} />
              </a>
            ))}
          </div>
        </div>

        {/* Products */}
        <div>
          <p className="text-white font-bold text-sm uppercase tracking-wider mb-4">Products</p>
          <ul className="space-y-2.5">
            {['Universal Kit', 'Epoxy Kit', 'Polyester Kit', 'Softboard Kit', 'Micro Kit', 'Bio-Resin Refill'].map((item) => (
              <li key={item}>
                <Link to="/products" className="text-stone-400 text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">{item}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <p className="text-white font-bold text-sm uppercase tracking-wider mb-4">Company</p>
          <ul className="space-y-2.5">
            {[{ label: 'About Us', to: '/about' }, { label: 'Where to Buy', to: '/where-to-buy' }, { label: 'How It Works', to: '/#how-it-works' }, { label: 'Surf Blog', to: '#' }].map((item) => (
              <li key={item.label}>
                <Link to={item.to} className="text-stone-400 text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Support */}
        <div>
          <p className="text-white font-bold text-sm uppercase tracking-wider mb-4">Support</p>
          <ul className="space-y-2.5 mb-6">
            {['FAQ', 'Shipping Info', 'Returns', 'Contact Us'].map((item) => (
              <li key={item}>
                <a href="#" className="text-stone-400 text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">{item}</a>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 text-stone-400 text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-sm" />
            </div>
            <span>hello@phixdoctor.com</span>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#1a4a40]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-stone-500 text-xs">
            © {new Date().getFullYear()} PHIX Doctor. All rights reserved.
          </p>
          <div className="flex gap-5">
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item) => (
              <a key={item} href="#" className="text-stone-500 text-xs hover:text-stone-300 cursor-pointer transition-colors">{item}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
